import React, { useState } from "react";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import { Range } from "react-range";
import { getNotificationQueues } from "adminApi/NotificationQueue/NotificationQueue";
import fields from "../../json/NotificationQueueFields.json";

const NotificationQueueTable = () => {
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const debouncedSearch = useDebouncedValue(search, 500);
    const sizeOptions = [10, 20, 50, 100];
    const { tabledata } = fields;

    const { data, meta, loading, error, refetch } = usePaginatedFetch(getNotificationQueues, {
        page,
        limit,
        search: debouncedSearch,
        title: "Notification Queue",
    });

    const displayFields = generateDisplayFields(tabledata);

    // transform data locally: render badges and progress bars for statuses/success
    const transform = (items) => {
        if (!Array.isArray(items)) return [];

        const statusBadge = (s) => {
            switch (s) {
                case "processing":
                    return (<span className="badge bg-warning text-dark">Processing</span>);
                case "pending":
                    return (<span className="badge bg-secondary">Pending</span>);
                case "completed":
                    return (<span className="badge bg-success">Completed</span>);
                case "failed":
                    return (<span className="badge bg-danger">Failed</span>);
                case "none":
                default:
                    return (<span className="badge bg-light text-dark">None</span>);
            }
        };

        const percentBar = (val) => {
            const num = parseFloat(val) || 0;
            const pct = Math.max(0, Math.min(100, Math.round(num * 100) / 100));
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                        <Range
                            step={1}
                            min={0}
                            max={100}
                            values={[pct]}
                            // non-interactive: no-op handler
                            onChange={() => { }}
                            renderTrack={({ props, children }) => (
                                <div
                                    onMouseDown={props.onMouseDown}
                                    onTouchStart={props.onTouchStart}
                                    style={{
                                        ...props.style,
                                        height: '6px',
                                        display: 'flex',
                                        width: '100%',
                                    }}
                                >
                                    <div
                                        ref={props.ref}
                                        style={{
                                            height: '6px',
                                            width: '100%',
                                            borderRadius: 4,
                                            background: `linear-gradient(90deg, #0d6efd ${pct}%, #e9ecef ${pct}%)`,
                                        }}
                                    >
                                        {children}
                                    </div>
                                </div>
                            )}
                            renderThumb={({ props }) => (
                                <div
                                    {...props}
                                    style={{
                                        ...props.style,
                                        height: 14,
                                        width: 14,
                                        borderRadius: 7,
                                        backgroundColor: '#0d6efd',
                                        boxShadow: '0 0 0 3px rgba(13,110,253,0.15)',
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div style={{ width: 50 }}>
                        <small className="text-muted">{pct}%</small>
                    </div>
                </div>
            );
        };

        return items.map((it) => ({
            ...it,
            email_status: statusBadge(it.email_status),
            push_status: statusBadge(it.push_status),
            email_success: percentBar(it.email_success),
            push_success: percentBar(it.push_success),
        }));
    };

    const transformedData = transform(data);

    const { formattedData, columns } = useTableConfig({
        data: transformedData,
        page,
        limit,
        refetch,
        IsDelete: false,
        IsEdit: false,
        config: {
            entityName: "Notification Queue",
            editUrl: "",
            idKey: "queue_id",
            deleteApi: () => { },
            displayFields,
        },
    });

    const handleSearch = (val, resetPage = null) => {
        setSearch(val);
        if (resetPage !== null) setPage(resetPage);
    };

    const onPageChange = (p) => setPage(p);
    const onLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    return (
        <DataTable
            columns={columns}
            data={formattedData}
            options={{
                loading,
                error,
                sortableColumns: displayFields.map((f) => f.accessor),
                copy: true,
                csv: true,
                pdf: true,
                print: true,
            }}
            Search={{
                search: true,
                SearchInputValue: search,
                handleSearch: handleSearch,
            }}
            Entries={{
                Entries: true,
                limit,
                onLimitChange,
                options: sizeOptions,
            }}
            pagination={{
                isActive: true,
                meta,
                onPageChange,
            }}
        />
    );
};

export default NotificationQueueTable;
